import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Users, Bell, Mail, Phone, MapPin, Wallet, ShoppingBag, Grid3x3, List, Loader2, Star } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import { useOrders } from '../../hooks/useOrders';
import { useCurrency } from '../../hooks/useCurrency';

interface MembersPageProps {
  onNavigate?: (page: string) => void;
}

const TIER_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  SILVER:   { label: 'Silver',   color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300', icon: '🥈' },
  GOLD:     { label: 'Gold',     color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400', icon: '🥇' },
  PLATINUM: { label: 'Platinum', color: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400', icon: '💎' },
};

export function MembersPage({ onNavigate }: MembersPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth >= 768;
    return true;
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const { customers: allCustomers, loading/*, stats */} = useCustomers();
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });
  const { formatCurrency } = useCurrency();

  // Members = SILVER, GOLD, or PLATINUM tier customers
  const members = allCustomers.filter((c) =>
    ['SILVER', 'GOLD', 'PLATINUM'].includes(c.membershipTier)
  );

  const totalRevenue = members.reduce((sum, m) => sum + parseFloat(m.totalSpent as any || '0'), 0);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="customers-members"
        onNavigate={onNavigate}
        ordersCount={ordersData.length}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Members List</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">Silver, Gold and Platinum tier customers</p>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <Grid3x3 className="w-4 h-4" /> Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <List className="w-4 h-4" /> List
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">
                    <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Members</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{loading ? '…' : members.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-xl">
                    <Bell className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Active Members</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {loading ? '…' : members.filter((m) => m.isActive).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
                    <Wallet className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {loading ? '…' : formatCurrency(totalRevenue)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading members…</p>
                </div>
              </div>
            )}

            {/* Members Grid View */}
            {!loading && viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {members.map((member) => {
                  const tierCfg = TIER_CONFIG[member.membershipTier];
                  const fullName = `${member.firstName} ${member.lastName}`;
                  const initials = `${member.firstName[0] ?? ''}${member.lastName[0] ?? ''}`;
                  return (
                    <div
                      key={member.id}
                      onClick={() => onNavigate?.(`customer-details-${member.id}`)}
                      className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-700 transition-all cursor-pointer"
                    >
                      {/* Member Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-white">{initials}</span>
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{fullName}</h3>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${tierCfg?.color}`}>
                              {tierCfg?.icon} {tierCfg?.label}
                            </span>
                          </div>
                        </div>
                        {member.isActive && (
                          <div className="p-1.5 bg-green-50 dark:bg-green-900/30 rounded-lg">
                            <Bell className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                        )}
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{member.email}</span>
                        </div>
                        {member.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">{member.phone}</span>
                          </div>
                        )}
                        {(member.city || member.state) && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {[member.city, member.state].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-1.5">
                          <Wallet className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(parseFloat(member.totalSpent as any || '0'))}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ShoppingBag className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {member._count?.orders ?? member.totalOrders ?? 0} orders
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-yellow-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">{member.loyaltyPoints} pts</span>
                        </div>
                      </div>

                      {/* Join Date */}
                      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        Joined {new Date(member.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Members List View */}
            {!loading && viewMode === 'list' && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                      {['#', 'Member', 'Contact', 'Location', 'Total Spent', 'Orders', 'Loyalty Pts', 'Join Date', 'Status', ''].map((h) => (
                        <th key={h} className="text-left px-6 py-3">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">{h}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const tierCfg = TIER_CONFIG[member.membershipTier];
                      const fullName = `${member.firstName} ${member.lastName}`;
                      const initials = `${member.firstName[0] ?? ''}${member.lastName[0] ?? ''}`;
                      return (
                        <tr
                          key={member.id}
                          onClick={() => onNavigate?.(`customer-details-${member.id}`)}
                          className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                        >
                          <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                            {member.customerNumber ? (
                              <button
                                onClick={() => onNavigate?.(`customer-details-${member.id}`)}
                                className="inline-flex items-center px-2 py-0.5 rounded-md font-mono text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                              >
                                {member.customerNumber}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                                <span className="text-sm font-bold text-white">{initials}</span>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-900 dark:text-white">{fullName}</p>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${tierCfg?.color}`}>
                                  {tierCfg?.icon} {tierCfg?.label}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <Mail className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">{member.email}</span>
                              </div>
                              {member.phone && (
                                <div className="flex items-center gap-1.5">
                                  <Phone className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                                  <span className="text-xs text-gray-600 dark:text-gray-400">{member.phone}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {[member.city, member.state].filter(Boolean).join(', ') || '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(parseFloat(member.totalSpent as any || '0'))}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {member._count?.orders ?? member.totalOrders ?? 0}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 text-yellow-400" />
                              <span className="text-xs text-gray-600 dark:text-gray-400">{member.loyaltyPoints}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {new Date(member.createdAt).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${member.isActive ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                              {member.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => onNavigate?.(`customer-details-${member.id}`)}
                              className="px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Empty State */}
            {!loading && members.length === 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Members Yet</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Customers with Silver, Gold, or Platinum membership tiers will appear here.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}